/**
 * Whitelist ключей TanStack Query для persist (localStorage).
 *
 * tRPC (createTRPCReact): `queryKey[0]` — массив `["module","proc"]`.
 * AuthProvider: строковый корень `"auth"`.
 *
 * Вынесено из `query-client.ts`, чтобы тесты не поднимали QueryClient/persister.
 */

/** Корни ключей вида `["auth","me"]` (не tRPC). */
export const PERSIST_STRING_ROOTS = new Set<string>(["auth"]);

/**
 * tRPC-пути cold-start гидрации (дашборд / списки, без которых reopen
 * всегда ждал сеть и показывал скелетон).
 */
export const PERSIST_TRPC_PATHS = new Set<string>([
	"recommendations.list",
	"progress.overview",
	"progress.getWeeklyStats",
	"courses.list",
]);

export function shouldPersistQueryKey(queryKey: readonly unknown[]): boolean {
	const root = queryKey[0];
	if (typeof root === "string") return PERSIST_STRING_ROOTS.has(root);
	if (Array.isArray(root) && root.every((p) => typeof p === "string")) {
		return PERSIST_TRPC_PATHS.has(root.join("."));
	}
	return false;
}

/**
 * Попадает ли запрос в снимок кэша.
 *
 * Свой `shouldDehydrateQuery` ЗАМЕНЯЕТ библиотечный, а не дополняет его, поэтому
 * условие на статус обязано быть здесь. У незавершённого (`pending`) запроса
 * `dehydrate` кладёт в снимок поле `promise`, `JSON.stringify` превращает его в
 * `{}`, и при следующем заходе `hydrate` падает на `{}.then` — «e.then is not a
 * function» (Hawk, OXEM-903). Данных у такого запроса ещё нет, терять нечего.
 *
 * Библиотечный `defaultShouldDehydrateQuery` не берём: он пропускает только
 * `success`, и `auth.me`, упавший при обновлении без сети, выпал бы из снимка
 * вместе с данными — офлайн-старт потерял бы пользователя из кэша.
 */
export function shouldPersistQuery(query: {
	queryKey: readonly unknown[];
	state: { status: string };
}): boolean {
	return query.state.status !== "pending" && shouldPersistQueryKey(query.queryKey);
}
